import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useMusicPlayer } from '../contexts/MusicPlayerContext';
import { 
  CheckCircle, 
  XCircle, 
  Music, 
  Search, 
  Filter,
  Play,
  Pause,
  AlertCircle,
  User,
  X,
  ChevronDown,
} from 'lucide-react';
import api from '../services/api';
import TrackDetailModal from '../components/TrackDetailModal';
import ArtistDetailModal from '../components/ArtistDetailModal';
import PresignedImage from '../components/PresignedImage';
import Toast from '../components/Toast/Toast';
import { usePendingApprovals } from '../contexts/PendingApprovalsContext';
import '../styles/library-page.css';

const Approvals = () => {
  const { user } = useAuth();
  const { refreshPendingCount } = usePendingApprovals();
  const { playTrack, currentTrack, isPlaying, playPause } = useMusicPlayer();
  const [submissions, setSubmissions] = useState([]);
  const [pendingArtists, setPendingArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterGenre, setFilterGenre] = useState('');

  const [processingId, setProcessingId] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [showMessage, setShowMessage] = useState({ show: false, type: '', message: '' });
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [showTrackModal, setShowTrackModal] = useState(false);
  const [showArtistModal, setShowArtistModal] = useState(false);

  useEffect(() => {
    if (user && user.role === 'admin') {
      fetchPendingItems();
    }
  }, [user]);

  const dismissMessage = () => setShowMessage({ show: false, type: '', message: '' });

  const fetchPendingItems = async () => {
    try {
      console.log('🔍 Fetching pending items...');
      
      // Fetch pending submissions
      const submissionsResponse = await api.get('/api/submissions/pending');
      setSubmissions(submissionsResponse.data.submissions);
      
      // Fetch pending artists
      const artistsResponse = await api.get('/api/artists/pending');
      setPendingArtists(artistsResponse.data.artists);
      
      console.log('📊 Submissions:', submissionsResponse.data.submissions);
      console.log('🎨 Artists:', artistsResponse.data.artists);
      
      // Debug: Log the first artist to see available fields
      if (artistsResponse.data.artists.length > 0) {
        console.log('🔍 First artist fields:', Object.keys(artistsResponse.data.artists[0]));
        console.log('🔍 First artist data:', artistsResponse.data.artists[0]);
      }
    } catch (error) {
      console.error('Failed to fetch pending items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (submission) => {
    const displayName = submission.title || submission.artist_name || 'this item';
    setConfirmAction({
      type: 'approve',
      submission,
      message: `Are you sure you want to approve "${displayName}"?`
    });
    setShowConfirmDialog(true);
  };

  const confirmApprove = async () => {
    const { submission } = confirmAction;
    setProcessingId(submission.id);
    setShowConfirmDialog(false);
    
    try {
      if (submission.type === 'artist') {
        await api.post(`/api/artists/${submission.id}/approve`);
        setPendingArtists(prev => prev.filter(a => a.id !== submission.id));
        setShowMessage({
          show: true,
          type: 'success',
          message: `"${submission.artist_name}" has been approved and is now available in the artists library.`
        });
      } else {
        await api.post(`/api/submissions/${submission.id}/approve`);
        setSubmissions(prev => prev.filter(s => s.id !== submission.id));
        setShowMessage({
          show: true,
          type: 'success',
          message: `"${submission.title}" has been approved and moved to the main tracks library.`
        });
      }
    } catch (error) {
      console.error('Failed to approve item:', error);
      setShowMessage({
        show: true,
        type: 'error',
        message: 'Failed to approve item. Please try again.'
      });
    } finally {
      setProcessingId(null);
      setConfirmAction(null);
      refreshPendingCount();
    }
  };

  const handleReject = async (submission) => {
    const displayName = submission.title || submission.artist_name || 'this item';
    setConfirmAction({
      type: 'reject',
      submission,
      message: `Please provide a reason for rejecting "${displayName}":`
    });
    setShowConfirmDialog(true);
  };

  const confirmReject = async (reason) => {
    if (!reason) return;
    
    const { submission } = confirmAction;
    setProcessingId(submission.id);
    setShowConfirmDialog(false);
    
    try {
      if (submission.type === 'artist') {
        await api.post(`/api/artists/${submission.id}/reject`);
        setPendingArtists(prev => prev.filter(a => a.id !== submission.id));
        setShowMessage({
          show: true,
          type: 'success',
          message: `"${submission.artist_name}" has been rejected.`
        });
      } else {
        await api.post(`/api/submissions/${submission.id}/reject`, { reason });
        setSubmissions(prev => prev.filter(s => s.id !== submission.id));
        setShowMessage({
          show: true,
          type: 'success',
          message: `"${submission.title}" has been rejected.`
        });
      }
    } catch (error) {
      console.error('Failed to reject item:', error);
      setShowMessage({
        show: true,
        type: 'error',
        message: 'Failed to reject item. Please try again.'
      });
    } finally {
      setProcessingId(null);
      setConfirmAction(null);
      refreshPendingCount();
    }
  };

  // Combine all pending items for counting
  const allPendingItems = [
    ...submissions,
    ...pendingArtists
  ];

  // Filter submissions by search and genre
  const filteredSubmissions = submissions.filter(submission => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = 
      submission.title?.toLowerCase().includes(searchLower) ||
      submission.artist_name?.toLowerCase().includes(searchLower) ||
      submission.featured_artist_name?.toLowerCase().includes(searchLower) ||
      submission.genre?.toLowerCase().includes(searchLower) ||
      submission.release_package?.toLowerCase().includes(searchLower);
    
    const matchesGenre = !filterGenre || submission.genre === filterGenre;
    
    return matchesSearch && matchesGenre;
  });

  // Filter artists by search
  const filteredArtists = pendingArtists.filter(artist => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = 
      artist.artist_name?.toLowerCase().includes(searchLower) ||
      artist.artist_legal_name?.toLowerCase().includes(searchLower) ||
      artist.bio?.toLowerCase().includes(searchLower);
    
    return matchesSearch;
  });

  // Get unique genres for filter dropdown (only from tracks)
  const uniqueGenres = [...new Set(submissions.map(submission => submission.genre).filter(Boolean))];

  const handleRowClick = (item, type) => {
    if (type === 'track') {
      setSelectedTrack(item);
      setShowTrackModal(true);
    } else {
      setSelectedArtist(item);
      setShowArtistModal(true);
    }
  };

  const closeTrackModal = () => {
    setShowTrackModal(false);
    setSelectedTrack(null);
  };

  const closeArtistModal = () => {
    setShowArtistModal(false);
    setSelectedArtist(null);
  };

  // Check if user is admin
  if (!user || user.role !== 'admin') {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 mx-auto mb-4 text-red-500" />
        <p className="text-gray-500 dark:text-gray-400">Access denied. Admin privileges required.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold-50"></div>
      </div>
    );
  }

  return (
    <div className="library-page library-page--transparent">
      <div className="library-page-header relative overflow-hidden group">
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/[0.095] to-transparent opacity-0 group-hover:opacity-100 transition-all duration-700 transform -translate-x-full group-hover:translate-x-full" />
        <div className="relative z-10 space-y-5 p-6">
          <div className="flex items-center justify-between gap-4">
            <h1 className="text-2xl font-bold text-white">Content Approvals</h1>
            <div className="text-sm text-white whitespace-nowrap">
              {allPendingItems.length === 0
                ? 'No pending items'
                : `${allPendingItems.length} pending`}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              <input
                type="text"
                placeholder="Search tracks, artist names, genres..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`pl-10 w-full border border-white/20 rounded-md focus:ring-2 focus:ring-gold-50 focus:border-gold-50 bg-black/50 text-white backdrop-blur-sm placeholder-gray-500 text-sm py-2 ${searchTerm ? 'pr-10' : 'pr-4'}`}
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors p-0.5"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="relative">
              <select
                value={filterGenre}
                onChange={(e) => setFilterGenre(e.target.value)}
                className="w-full appearance-none pl-4 pr-10 py-2 border border-white/20 rounded-md focus:ring-2 focus:ring-gold-50 focus:border-gold-50 bg-black/50 text-white backdrop-blur-sm text-sm"
              >
                <option value="">All Genres</option>
                {uniqueGenres.map((genre) => (
                  <option key={genre} value={genre}>{genre}</option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
            </div>

            <div className="text-gray-400 flex items-center text-sm">
              <Filter className="h-4 w-4 mr-2 flex-shrink-0" />
              {filteredSubmissions.length + filteredArtists.length} of {allPendingItems.length} shown
            </div>
          </div>
        </div>
      </div>

      <div className="library-page-scroll">
        {allPendingItems.length === 0 ? (
          <div className="library-page-panel p-12 text-center">
            <Music className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-400">No pending items to review</p>
            <p className="text-sm text-gray-500 mt-1">
              All submissions have been processed or no new submissions have been made yet
            </p>
          </div>
        ) : filteredSubmissions.length === 0 && filteredArtists.length === 0 ? (
          <div className="library-page-panel p-12 text-center">
            <Search className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-400">No matching pending items</p>
            <p className="text-sm text-gray-500 mt-1">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="library-page-panel">
            {filteredSubmissions.length > 0 && (
              <div className="library-page-section">
                <div className="library-page-section-header">
                  <h2 className="text-lg font-semibold text-white">Pending Track Submissions</h2>
                  <p className="text-sm text-gray-400 mt-0.5">Review and approve or reject track submissions</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="library-page-table min-w-full divide-y divide-white/10">
                    <thead>
                      <tr>
                        <th>Track Info</th>
                        <th>Artist & Release</th>
                        <th>Genre & Details</th>
                        <th>User</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/10">
                                      {filteredSubmissions.map((submission) => (
                    <tr 
                      key={submission.id} 
                      className="hover:bg-white/5 cursor-pointer"
                      onClick={() => handleRowClick(submission, 'track')}
                    >
                        {/* Track Info */}
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-3">
                            {/* Play/Pause Button */}
                            {submission.url ? (
                              currentTrack && currentTrack.id === submission.id && isPlaying ? (
                                <button
                                  onClick={playPause}
                                  className="h-8 w-8 rounded-lg bg-gold-50 flex items-center justify-center hover:bg-gold-100 transition-colors"
                                  title="Pause track"
                                >
                                  <Pause className="h-4 w-4 text-white" />
                                </button>
                              ) : (
                                <button
                                  onClick={() => playTrack(submission, filteredSubmissions)}
                                  className="h-8 w-8 rounded-lg bg-gold-50/50 flex items-center justify-center hover:bg-gold-100 transition-colors"
                                  title="Play track"
                                >
                                  <Play className="h-4 w-4 text-white" />
                                </button>
                              )
                            ) : (
                              <div className="h-8 w-8 rounded-lg bg-gray-700 flex items-center justify-center">
                                <Music className="h-4 w-4 text-gray-400" />
                              </div>
                            )}
                            
                            <div>
                              <div className="text-sm font-medium text-white">
                                {submission.title || 'Untitled'}
                              </div>
                              <div className="text-xs text-gray-400">
                                {submission.duration || 'N/A'}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Artist & Release */}
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            <div className="text-sm text-white">
                              {submission.artist_name || 'Unknown Artist'}
                            </div>
                            {submission.featured_artist_name && (
                              <div className="text-xs text-gray-400">
                                Feat. {submission.featured_artist_name}
                              </div>
                            )}
                            {submission.release_package && (
                              <div className="text-xs text-gray-400">
                                {submission.release_package}
                              </div>
                            )}
                            {submission.created_at && (
                              <div className="text-xs text-gray-400">
                                Submitted {new Date(submission.created_at).toLocaleDateString()}
                              </div>
                            )}
                          </div>
                        </td>

                        {/* Genre & Details */}
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            {submission.genre && (
                              <span className="inline-flex px-2 py-1 text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded">
                                {submission.genre}
                              </span>
                            )}
                            {submission.bpm && (
                              <div className="text-xs text-gray-400">
                                {submission.bpm} BPM
                              </div>
                            )}
                            {submission.song_key && (
                              <div className="text-xs text-gray-400">
                                Key: {submission.song_key}
                              </div>
                            )}
                          </div>
                        </td>

                        {/* User */}
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <User className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-white">
                              {submission.user_email || 'Unknown User'}
                            </span>
                          </div>
                        </td>

                        {/* Status */}
                        <td className="px-6 py-4">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                            Pending Review
                          </span>
                        </td>

                        {/* Actions */}
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleApprove(submission);
                              }}
                              disabled={processingId === submission.id}
                              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Approve
                            </button>
                            
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReject(submission);
                              }}
                              disabled={processingId === submission.id}
                              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              Reject
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </div>
            )}

            {filteredArtists.length > 0 && (
              <div className="library-page-section">
                <div className="library-page-section-header">
                  <h2 className="text-lg font-semibold text-white">Pending Artist Submissions</h2>
                  <p className="text-sm text-gray-400 mt-0.5">Review and approve or reject artist submissions</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="library-page-table min-w-full divide-y divide-white/10">
                    <thead>
                      <tr>
                        <th>Artist Info</th>
                        <th>Legal Name & Bio</th>
                        <th>Social Media</th>
                        <th>User</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/10">
                                      {filteredArtists.map((artist) => (
                    <tr 
                      key={artist.id} 
                      className="hover:bg-white/5 cursor-pointer"
                      onClick={() => handleRowClick(artist, 'artist')}
                    >
                        {/* Artist Info */}
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-3">
                            {artist.image ? (
                              <div className="h-8 w-8 rounded-lg overflow-hidden flex-shrink-0">
                                <PresignedImage
                                  assetKey={artist.image}
                                  assetType="artist"
                                  status="pending"
                                  alt={`${artist.artist_name || 'Artist'} thumbnail`}
                                  className="h-8 w-8 object-cover"
                                />
                              </div>
                            ) : (
                              <div className="h-8 w-8 rounded-lg bg-gray-700 flex items-center flex-shrink-0">
                                <Music className="h-4 w-4 text-gray-400" />
                              </div>
                            )}
                            <div>
                              <div className="text-sm font-medium text-white">
                                {artist.artist_name || 'Untitled Artist'}
                              </div>
                              <div className="text-xs text-gray-400">
                                {artist.created_at ? `Submitted ${new Date(artist.created_at).toLocaleDateString()}` : 'N/A'}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Legal Name & Bio */}
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            <div className="text-sm text-white">
                              {artist.artist_legal_name || 'N/A'}
                            </div>
                            {artist.bio && (
                              <div className="text-xs text-gray-400 line-clamp-2">
                                {artist.bio.length > 100 ? `${artist.bio.substring(0, 100)}...` : artist.bio}
                              </div>
                            )}
                          </div>
                        </td>

                        {/* Social Media */}
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            {artist.artist_fb_url && (
                              <div className="text-xs text-blue-400">
                                <a href={artist.artist_fb_url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                                  Facebook
                                </a>
                              </div>
                            )}
                            {artist.artist_ig_url && (
                              <div className="text-xs text-pink-400">
                                <a href={artist.artist_ig_url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                                  Instagram
                                </a>
                              </div>
                            )}
                          </div>
                        </td>

                        {/* User */}
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <User className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-white">
                              {artist.user_email || artist.email || artist.user?.email || artist.submitted_by || 'Unknown User'}
                            </span>
                          </div>
                        </td>

                        {/* Status */}
                        <td className="px-6 py-4">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                            Pending Review
                          </span>
                        </td>

                        {/* Actions */}
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleApprove({ ...artist, type: 'artist' });
                              }}
                              disabled={processingId === artist.id}
                              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Approve
                            </button>
                            
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReject({ ...artist, type: 'artist' });
                              }}
                              disabled={processingId === artist.id}
                              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              Reject
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              </div>
            )}
          </div>
        )}
      </div>

      <TrackDetailModal
        track={selectedTrack}
        isOpen={showTrackModal}
        onClose={closeTrackModal}
        user={user}
      />

      <ArtistDetailModal
        artist={selectedArtist}
        isOpen={showArtistModal}
        onClose={closeArtistModal}
        user={user}
      />

      {showConfirmDialog && (
           <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
             <div className="relative bg-black/90 border border-white/20 rounded-lg p-6 max-w-md w-full mx-4 shadow-[0_4px_4px_rgba(0,0,0,0.17)]">
               <div className="relative">
                 <h3 className="text-lg font-medium text-white mb-4">
                   {confirmAction?.type === 'approve' ? 'Confirm Approval' : 'Confirm Rejection'}
                 </h3>
                 
                 {confirmAction?.type === 'approve' ? (
                   <div>
                     <p className="text-gray-300 mb-6">
                       {confirmAction.message}
                     </p>
                     <div className="flex space-x-3">
                       <button
                         onClick={() => setShowConfirmDialog(false)}
                         className="flex-1 px-4 py-2 border border-white/20 rounded-md text-gray-300 hover:text-white hover:bg-white/10 transition-colors"
                       >
                         Cancel
                       </button>
                       <button
                         onClick={confirmApprove}
                         className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                       >
                         Approve
                       </button>
                     </div>
                   </div>
                 ) : (
                   <div>
                     <p className="text-gray-300 mb-4">
                       {confirmAction.message}
                     </p>
                     <input
                       type="text"
                       id="rejection-reason"
                       placeholder="Enter rejection reason..."
                       className="w-full px-3 py-2 border border-white/20 rounded-md mb-6 bg-black/50 text-white backdrop-blur-sm placeholder-gray-500"
                       onKeyPress={(e) => {
                         if (e.key === 'Enter') {
                           const reason = e.target.value.trim();
                           if (reason) confirmReject(reason);
                         }
                       }}
                     />
                     <div className="flex space-x-3">
                       <button
                         onClick={() => setShowConfirmDialog(false)}
                         className="flex-1 px-4 py-2 border border-white/20 rounded-md text-gray-300 hover:text-white hover:bg-white/10 transition-colors"
                       >
                         Cancel
                       </button>
                       <button
                         onClick={() => {
                           const reason = document.getElementById('rejection-reason').value.trim();
                           if (reason) confirmReject(reason);
                         }}
                         className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                       >
                         Reject
                       </button>
                     </div>
                   </div>
                 )}
               </div>
             </div>
           </div>
      )}

      <Toast
        show={showMessage.show}
        type={showMessage.type}
        message={showMessage.message}
        onDismiss={dismissMessage}
      />
    </div>
  );
};

export default Approvals;
